import { Static, Type } from '@sinclair/typebox';
import { CrmCallOutcome } from './crm-queue';

// =====================================================================
// CRM — suivi en direct d'un appel sur la ligne OVH de la CSM (événements temps réel OVH).
// La fiche d'appel écoute la ligne dès son ouverture : sonnerie, décroché, raccroché vers le
// numéro de la personne. Elle en déduit l'heure de début, la durée et une issue probable, que la
// CSM vérifie avant d'enregistrer. Rien de la conversation n'est capté : seulement ces instants.
// =====================================================================

export enum CrmLiveCallEventKind {
  /** Ça sonne chez la personne. */
  RINGING = 'RINGING',
  /** Fin de sonnerie (sans décroché, ou juste avant le décroché). */
  RINGING_ENDED = 'RINGING_ENDED',
  ANSWERED = 'ANSWERED',
  ENDED = 'ENDED',
}

export const AdminCrmLiveCallEventSchema = Type.Object({
  call_id: Type.String(),
  kind: Type.Enum(CrmLiveCallEventKind),
  /** ISO complet. */
  at: Type.String(),
});
export type AdminCrmLiveCallEvent = Static<typeof AdminCrmLiveCallEventSchema>;

export const AdminCrmLiveCallResponseSchema = Type.Object({
  /** `false` : pas de ligne OVH pour cet admin (ou pas de numéro) ; la fiche arrête d'écouter. */
  enabled: Type.Boolean(),
  /** Curseur OVH, à renvoyer à l'appel suivant. */
  session: Type.Optional(Type.String()),
  /** Événements de la ligne qui visent le numéro de la personne, dans l'ordre. */
  events: Type.Array(AdminCrmLiveCallEventSchema),
});
export type AdminCrmLiveCallResponse = Static<typeof AdminCrmLiveCallResponseSchema>;

export enum CrmLiveCallStatus {
  IDLE = 'IDLE',
  RINGING = 'RINGING',
  IN_CALL = 'IN_CALL',
  ENDED_ANSWERED = 'ENDED_ANSWERED',
  ENDED_UNANSWERED = 'ENDED_UNANSWERED',
}

export interface CrmLiveCallState {
  status: CrmLiveCallStatus;
  call_id?: string;
  ringing_at?: string;
  answered_at?: string;
  ended_at?: string;
}

export const CRM_LIVE_CALL_IDLE: CrmLiveCallState = { status: CrmLiveCallStatus.IDLE };

/** En dessous, un décroché ressemble à un répondeur : aucune issue n'est proposée. */
export const CRM_LIVE_CALL_SHORT_ANSWER_S = 30;

const isEnded = (status: CrmLiveCallStatus): boolean =>
  status === CrmLiveCallStatus.ENDED_ANSWERED || status === CrmLiveCallStatus.ENDED_UNANSWERED;

/**
 * Applique un événement à l'état de l'appel suivi. La fiche suit le premier appel vu ; un autre
 * appel n'est pris qu'une fois le premier terminé (la CSM rappelle après un « pas de réponse »).
 */
export function reduceCrmLiveCall(state: CrmLiveCallState, event: AdminCrmLiveCallEvent): CrmLiveCallState {
  let current = state;
  if (current.call_id !== undefined && current.call_id !== event.call_id) {
    const startsNewCall = event.kind === CrmLiveCallEventKind.RINGING || event.kind === CrmLiveCallEventKind.ANSWERED;
    if (!isEnded(current.status) || !startsNewCall) return current;
    current = CRM_LIVE_CALL_IDLE;
  }

  switch (event.kind) {
    case CrmLiveCallEventKind.RINGING:
      if (current.status !== CrmLiveCallStatus.IDLE) return current;
      return { status: CrmLiveCallStatus.RINGING, call_id: event.call_id, ringing_at: event.at };
    case CrmLiveCallEventKind.ANSWERED:
      // La fin de sonnerie peut arriver avant le décroché : un « pas de réponse » est alors corrigé.
      if (current.status === CrmLiveCallStatus.IN_CALL || current.status === CrmLiveCallStatus.ENDED_ANSWERED) {
        return current;
      }
      return {
        status: CrmLiveCallStatus.IN_CALL,
        call_id: event.call_id,
        ringing_at: current.ringing_at ?? event.at,
        answered_at: event.at,
      };
    case CrmLiveCallEventKind.RINGING_ENDED:
      if (current.status !== CrmLiveCallStatus.RINGING) return current;
      return { ...current, status: CrmLiveCallStatus.ENDED_UNANSWERED, ended_at: event.at };
    case CrmLiveCallEventKind.ENDED:
      if (current.status === CrmLiveCallStatus.IN_CALL) {
        return { ...current, status: CrmLiveCallStatus.ENDED_ANSWERED, ended_at: event.at };
      }
      if (current.status === CrmLiveCallStatus.RINGING) {
        return { ...current, status: CrmLiveCallStatus.ENDED_UNANSWERED, ended_at: event.at };
      }
      return current;
    default:
      return current;
  }
}

/** Durée de la conversation (décroché → raccroché), en secondes ; `null` tant qu'elle n'est pas finie. */
export function crmLiveCallDurationS(state: CrmLiveCallState): number | null {
  if (state.status !== CrmLiveCallStatus.ENDED_ANSWERED || !state.answered_at || !state.ended_at) return null;
  return Math.max(0, Math.round((Date.parse(state.ended_at) - Date.parse(state.answered_at)) / 1000));
}

/** Issue proposée à la fin de l'appel : la CSM la vérifie (un rappel demandé reste à cocher par elle). */
export function suggestCrmCallOutcome(state: CrmLiveCallState): CrmCallOutcome | null {
  if (state.status === CrmLiveCallStatus.ENDED_UNANSWERED) return CrmCallOutcome.NO_ANSWER;
  const duration = crmLiveCallDurationS(state);
  if (duration !== null && duration >= CRM_LIVE_CALL_SHORT_ANSWER_S) return CrmCallOutcome.REACHED;
  return null;
}
